import * as constants from '../../../common/constants';
import {
  getPxeBasePath,
  isPreviewAssetForBook,
  isPreviewTocFetchInFlightForBook,
  isPreviewTocLoadedForBook,
  isRplusEpubTitleType,
  shouldShowRplusEpubBookDetailPreview,
  shouldSkipPreviewTocFetch
} from './RplusEpubPreviewUtils';

export const isBookDetailForRoute = (bookDetail, bookid) =>
  bookDetail?.bookID != null && String(bookDetail.bookID) === String(bookid);

export const isPxeTitleType = (titleType) => constants.PXE_PREVIEW_TITLE_TYPES.includes(titleType);

export const hasPxePreviewContent = (asset) => Boolean(asset?.slates?.length);

export const isPxeBookDetailForRoute = (bookDetail, bookid) =>
  isBookDetailForRoute(bookDetail, bookid) && isPxeTitleType(bookDetail.titleType);

export const isPxeTocReadyToFetch = (bookDetail, bookid) =>
  isPxeBookDetailForRoute(bookDetail, bookid) && Boolean(bookDetail.pulseBookID && bookDetail.basePath);

export const getPxeTocFetchKey = (bookid, pulseBookId) => `${bookid}:${pulseBookId}`;

export const isPxeTocActiveForBook = (bookDetail, authoringBookId, pulseBookId) =>
  isPxeBookDetailForRoute(bookDetail, authoringBookId) &&
  pulseBookId != null &&
  String(bookDetail.pulseBookID) === String(pulseBookId);

export const isPxeTocLoadedForBook = (asset, bookid, pxeTocStatus) =>
  isPreviewTocLoadedForBook(asset, bookid, pxeTocStatus, hasPxePreviewContent);

export const shouldSkipPxeTocFetch = (asset, bookid, pxeTocStatus, tocFetchKey, cachedFetchKey) =>
  shouldSkipPreviewTocFetch(asset, bookid, pxeTocStatus, tocFetchKey, cachedFetchKey, hasPxePreviewContent);

export const isPxeTocFetchInFlightForBook = (asset, bookid, pxeTocStatus) =>
  isPreviewTocFetchInFlightForBook(asset, bookid, pxeTocStatus);

export const fetchPxeTocForBookDetail = (asset, bookDetail, authoringBookId) => {
  asset.fetchPxeToc({
    bookId: bookDetail.pulseBookID,
    authoringBookId,
    globalBookId: bookDetail.globalBookID,
    bookBasePath: bookDetail.basePath
  });
};

// Book Detail passes cachedFetchKey.
export const loadPxeTocIfNeeded = (asset, bookDetail, bookid, pxeTocStatus, cachedFetchKey = null) => {
  if (!isPxeTocReadyToFetch(bookDetail, bookid) || isPxeTocFetchInFlightForBook(asset, bookid, pxeTocStatus)) {
    return cachedFetchKey;
  }

  const tocFetchKey = getPxeTocFetchKey(bookid, bookDetail.pulseBookID);

  if (shouldSkipPxeTocFetch(asset, bookid, pxeTocStatus, tocFetchKey, cachedFetchKey)) {
    return cachedFetchKey;
  }

  if (isPreviewAssetForBook(asset, bookid) && hasPxePreviewContent(asset)) {
    return tocFetchKey;
  }

  fetchPxeTocForBookDetail(asset, bookDetail, bookid);
  return tocFetchKey;
};

export const shouldShowBookDetailPreview = (bookDetail, bookid, asset, pxeTocStatus, epubAsset, epubTocStatus) => {
  if (!isBookDetailForRoute(bookDetail, bookid)) {
    return false;
  }
  if (isRplusEpubTitleType(bookDetail.titleType)) {
    return shouldShowRplusEpubBookDetailPreview(epubAsset, bookid, epubTocStatus);
  }
  if (!isPxeTitleType(bookDetail.titleType)) {
    return true;
  }
  return isPxeTocLoadedForBook(asset, bookid, pxeTocStatus) && hasPxePreviewContent(asset);
};

export { getBookFolderPath, getPxeBasePath } from './RplusEpubPreviewUtils';

// Prefer label, fall back to title (rplus.toc_json).
export const getTocEntryLabel = (entry) => {
  if (entry?.label != null && String(entry.label).trim() !== '') {
    return String(entry.label).trim();
  }
  if (entry?.title != null && String(entry.title).trim() !== '') {
    return String(entry.title).trim();
  }
  return '';
};

export const resolvePxeTocEntryId = (entry, fallbackPlayOrder) => {
  if (entry?.id != null && String(entry.id).trim() !== '') {
    return String(entry.id).trim();
  }
  if (entry?.href) {
    return entry.href;
  }
  return String(fallbackPlayOrder);
};

export const normalizePxeTocEntries = (tocPayload) => {
  let toc = tocPayload;
  if (typeof toc === 'string') {
    try {
      toc = JSON.parse(toc);
    } catch (error) {
      return [];
    }
  }
  if (Array.isArray(toc)) {
    return toc;
  }
  if (toc && Array.isArray(toc.children)) {
    return toc.children;
  }
  return [];
};

export const resolvePxeToc = (entries, basePath) =>
  entries.map((entry) => {
    const url = `${basePath}${entry.href}`;
    const displayTitle = getTocEntryLabel(entry);
    const slatePlayOrder = entry.playOrder != null ? entry.playOrder : undefined;
    const resolved = {
      ...entry,
      id: resolvePxeTocEntryId(entry, slatePlayOrder),
      url,
      uri: url,
      linkUri: entry.href,
      title: displayTitle,
      label: displayTitle
    };
    if (entry.children?.length) {
      resolved.children = resolvePxeToc(entry.children, basePath);
    }
    return resolved;
  });

const flattenPxeToc = (entries, callback) => {
  entries.forEach((entry) => {
    callback(entry);
    if (entry.children?.length) {
      flattenPxeToc(entry.children, callback);
    }
  });
};

// Slate id must match TOC id (VegaReader playlist lookup).
export const buildPxeSlatesFromToc = (entries, basePath) => {
  const slates = [];
  let playOrder = 0;
  flattenPxeToc(entries, (entry) => {
    if (!entry?.href) {
      return;
    }
    playOrder += 1;
    const slatePlayOrder = entry.playOrder != null ? entry.playOrder : playOrder;
    const slateId = resolvePxeTocEntryId(entry, slatePlayOrder);
    const displayTitle = getTocEntryLabel(entry);
    slates.push({
      playOrder: slatePlayOrder,
      id: slateId,
      href: entry.href,
      title: displayTitle,
      type: 'page',
      uri: `${basePath}${entry.href}`,
      linkUri: entry.href
    });
  });
  return slates;
};

export const setPxeAsset = (asset, bookId, globalBookId, tocEntries = [], bookBasePath = '') => {
  const basePath = getPxeBasePath(bookBasePath);
  const toc = normalizePxeTocEntries(tocEntries);
  asset.set({
    data: {
      bookId,
      globalBookId,
      basePath,
      slates: buildPxeSlatesFromToc(toc, basePath),
      children: {
        children: resolvePxeToc(toc, basePath)
      }
    }
  });
};

export const getFirstPxePageId = (asset) => asset.slates[0]?.playOrder;
